import { Controller } from "@hotwired/stimulus";

// Reusable drawer: slides in from the right, backdrop, close on outside click or Escape.
// Use with Ui::DrawerComponent for consistent markup.
export default class extends Controller {
  static targets = ["backdrop", "panel"];
  static values = { frameId: String, overlay: Boolean };

  connect() {
    this.hideElements();
    this.boundCloseWithEscape = this.closeWithEscape.bind(this);
    this.boundOpenOnEvent = this.openOnDrawerEvent.bind(this);
    this.boundCloseOnEvent = this.close.bind(this);
    this.boundCloseOthers = this.closeIfNotTarget.bind(this);
    document.addEventListener("drawer:open", this.boundOpenOnEvent);
    document.addEventListener("drawer:close", this.boundCloseOnEvent);
    document.addEventListener("drawer:close-others", this.boundCloseOthers);
  }

  disconnect() {
    document.removeEventListener("drawer:open", this.boundOpenOnEvent);
    document.removeEventListener("drawer:close", this.boundCloseOnEvent);
    document.removeEventListener("drawer:close-others", this.boundCloseOthers);
    document.removeEventListener("keydown", this.boundCloseWithEscape);
    this.unlockBodyScroll();
  }

  // Open only when drawer:open targets our frame (or has no target for single-drawer pages)
  openOnDrawerEvent(event) {
    const targetFrameId = event.detail?.frameId;
    if (targetFrameId != null && targetFrameId !== "") {
      if (this.hasFrameIdValue && targetFrameId === this.frameIdValue) this.open();
    } else if (!this.hasFrameIdValue) {
      this.open();
    }
  }

  // Open only when OUR frame renders from a fetch (prevents multiple drawers opening)
  // turbo:frame-render fires on the frame; event.target is that frame. Skip initial page load (no fetchResponse).
  openOnFrameRender(event) {
    const frameElement = event.target;
    if (!frameElement?.id || frameElement.tagName?.toLowerCase() !== "turbo-frame") return;
    // Only open when frame received content from a fetch, not on initial page load
    if (!event.detail?.fetchResponse) return;
    if (!this.hasFrameIdValue || frameElement.id !== this.frameIdValue) return;
    // Only open if the frame is inside our drawer (prevents opening wrong drawer when multiple exist)
    if (!this.element.contains(frameElement)) return;
    // Skip if already open (prevents double-open when drawer:open and turbo:frame-render both fire)
    if (this.panelTarget.classList.contains("translate-x-0")) return;
    this.open();
  }

  open() {
    if (this.panelTarget.classList.contains("translate-x-0")) return; // Already open
    // Close other drawers first (unless we're an overlay—we pop over, don't replace)
    if (!this.overlayValue) {
      document.dispatchEvent(new CustomEvent("drawer:close-others", { detail: { exceptFrameId: this.frameIdValue } }));
    }
    this.toggleClasses(true);
    document.addEventListener("keydown", this.boundCloseWithEscape);
    this.lockBodyScroll();
  }

  close() {
    this.toggleClasses(false);
    document.removeEventListener("keydown", this.boundCloseWithEscape);
    this.unlockBodyScroll();
    if (this.hasBackdropTarget) {
      setTimeout(() => this.backdropTarget.classList.add("hidden"), 200);
    }
  }

  lockBodyScroll() {
    this._scrollY = window.scrollY ?? window.pageYOffset;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
  }

  unlockBodyScroll() {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    if (typeof this._scrollY === "number") {
      window.scrollTo(0, this._scrollY);
    }
  }

  toggle(event) {
    if (event) event.stopPropagation();
    const isOpen = this.panelTarget.classList.contains("translate-x-0");
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  closeWithEscape(event) {
    if (event.key === "Escape") this.close();
  }

  // Close this drawer when drawer:close-others says another drawer is opening (except us)
  closeIfNotTarget(event) {
    const exceptFrameId = event.detail?.exceptFrameId;
    if (exceptFrameId != null && exceptFrameId !== this.frameIdValue) {
      this.close();
    }
  }

  closeIfOutside(event) {
    // Ignore clicks from drawer openers (prevents flicker: open → same click bubbles → close)
    if (event.target.closest('[data-controller*="drawer-opener"]')) return;
    // Ignore clicks inside any drawer panel (e.g. user interacting with overlay drawer on top)
    if (event.target.closest('[data-drawer-target="panel"]')) return;
    // Ignore clicks inside turbo-frames (drawer content; e.g. day links in booking drawer)
    if (event.target.closest("turbo-frame")) return;

    if (
      !this.panelTarget.contains(event.target) &&
      this.panelTarget.classList.contains("translate-x-0")
    ) {
      this.close();
    }
  }

  hideElements() {
    if (!this.hasPanelTarget) return;
    this.panelTarget.classList.add("translate-x-full");
    this.panelTarget.classList.remove("translate-x-0");
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden", "opacity-0");
    }
    document.removeEventListener("keydown", this.boundCloseWithEscape);
    this.unlockBodyScroll();
  }

  toggleClasses(isOpen) {
    this.panelTarget.classList.toggle("translate-x-full", !isOpen);
    this.panelTarget.classList.toggle("translate-x-0", isOpen);
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.toggle("hidden", !isOpen);
      this.backdropTarget.classList.toggle("opacity-0", !isOpen);
      this.backdropTarget.classList.toggle("opacity-100", isOpen);
    }
  }
}
